import React, { useCallback, useMemo, useState } from "react";
import { Select, Tooltip } from "antd";
import { useIsMobile } from "../hooks/useIsMobile";
import { TbMessageCircle } from "react-icons/tb";
import { RiEmotionLine, RiGroupLine, RiShutDownLine, RiSettings5Line, RiBankCardLine } from "react-icons/ri";
import styles from "./Sidebar.module.css";
import { PRICING_DATA } from "../constants/PricingConstants";

function ContactOption({ contact }) {
  const unreadCount = Number(contact.unread_count) || 0;
  const isUnreadReaction =
    contact.last_activity_type === "reaction" && unreadCount > 0;

  return (
    <div className={styles.contactOption}>
      <div className={styles.contactText}>
        <span className={styles.contactName}>{contact.nameOnly || "Unknown"}</span>
        <span className={styles.contactPhone}>{contact.phone}</span>
        {contact.activity_preview ? (
          <span
            className={
              isUnreadReaction
                ? `${styles.contactActivity} ${styles.contactActivityUnreadReaction}`
                : styles.contactActivity
            }
          >
            {contact.last_activity_type === "reaction" ? (
              <RiEmotionLine className={styles.contactActivityIcon} size={13} />
            ) : null}
            <span>{contact.activity_preview}</span>
          </span>
        ) : null}
      </div>
      {unreadCount > 0 ? (
        <span className={styles.contactUnreadBadge}>{unreadCount}</span>
      ) : null}
    </div>
  );
}

export default function Sidebar({
  contacts,
  currentPhone,
  onSelectContact,
  isLive,
  setIsLive,
  disableLiveToggle,
  isAdmin = true,
  onLogout,
  onOpenTeam,
  onOpenSubscription,
  onOpenSettings,
  isWabaConnected = false,
  mobileUnreadPulseLeft = false,
  userProfile = null,
}) {
  const isMobile = useIsMobile(700);
  const liveColor = isLive ? "#25d366" : "#ccc";
  const [contactSearch, setContactSearch] = useState("");

  const totalUnread = useMemo(() => {
    return (contacts || []).reduce(
      (sum, contact) => sum + (Number(contact.unread_count) || 0),
      0
    );
  }, [contacts]);

  const planHierarchy = useMemo(() => {
    const planData = userProfile?.Plan || {};
    const pName = (planData.plan || planData.Plan || "").toLowerCase();
    
    if (pName) {
      const tierKey = Object.keys(PRICING_DATA).find(k => k.toLowerCase() === pName);
      if (tierKey && PRICING_DATA[tierKey]) {
        return PRICING_DATA[tierKey].planHierarchy || 0;
      }
    }
    return 0;
  }, [userProfile]);

  const hasAnyUnread = totalUnread > 0;

  const selectOptions = useMemo(() => {
    return (contacts || []).map((contact) => ({
      value: contact.phone,
      label: <ContactOption contact={contact} />,
      searchText: `${contact.nameOnly || ""} ${contact.phone || ""} ${contact.fullLine || ""}`,
    }));
  }, [contacts]);

  const pickContact = useCallback(
    (phone) => {
      if (phone != null && phone !== "") {
        setContactSearch("");
        onSelectContact(phone);
      }
    },
    [onSelectContact]
  );

  return (
    <div id="sidebar" className={styles.sidebar}>
      {mobileUnreadPulseLeft ? (
        <span
          className={styles.mobileUnreadIndicator}
          title="Unread messages"
          aria-hidden
        >
          <TbMessageCircle size={18} />
        </span>
      ) : null}
      <div className={styles.body}>
        <div className={styles.header}>
          <div className={styles.titleWrap}>
            <span className={styles.title}>Chats</span>
            <span
              id="globalUnread"
              className={styles.globalUnread}
              style={{ display: hasAnyUnread ? "inline-flex" : "none" }}
              title={`${totalUnread} unread message${totalUnread === 1 ? "" : "s"}`}
              aria-label={`${totalUnread} unread message${totalUnread === 1 ? "" : "s"}`}
            >
              <TbMessageCircle size={15} />
              <span className={styles.globalUnreadCount}>
                {totalUnread > 99 ? "99+" : totalUnread}
              </span>
            </span>
          </div>
          <div className={styles.liveControls}>
            <div
              id="pulse-sidebar"
              style={{
                width: 7,
                height: 7,
                background: liveColor,
                borderRadius: "50%",
              }}
              className={isLive ? "blinking" : ""}
            />
            <label className="switch" aria-label="Toggle live status">
              <input
                type="checkbox"
                checked={isLive}
                onChange={() => setIsLive(!isLive)}
                disabled={disableLiveToggle}
              />
              <span className="slider" />
            </label>
          </div>
        </div>

        <div className={styles.searchContainer}>
          <Select
            data-testid="sidebar-contact-select"
            className={styles.contactSelect}
            popupClassName={styles.contactSelectDropdown}
            placeholder="Select a contact..."
            value={contactSearch ? undefined : currentPhone || undefined}
            searchValue={contactSearch}
            onSearch={setContactSearch}
            onChange={(v) => pickContact(v)}
            onSelect={(v) => pickContact(v)}
            options={selectOptions}
            showSearch
            autoClearSearchValue
            optionFilterProp="label"
            listHeight={isMobile ? 260 : 300}
            popupMatchSelectWidth
            style={{ width: "100%" }}
            optionLabelProp="label"
            open={contactSearch ? true : undefined}
            filterOption={(input, option) =>
              String(option?.searchText ?? "")
                .toLowerCase()
                .includes(input.toLowerCase()) ||
              String(option?.value ?? "").includes(input.trim())
            }
          />
        </div>
      </div>

      <div className={styles.footer}>
        <Tooltip title="Log out">
          <button
            data-testid="sidebar-logout-button"
            type="button"
            className={`${styles.footerIconButton} ${styles.logoutButton}`}
            onClick={() => {
              if (onLogout) onLogout();
            }}
            aria-label="Log out"
          >
            <RiShutDownLine size={24}/>
          </button>
        </Tooltip>

        {isAdmin && (
          <>
            {planHierarchy > 1 && (
              <Tooltip title={isWabaConnected ? "Team" : "Connect WhatsApp to enable Team management"}>
                <button
                  data-testid="sidebar-team-button"
                  type="button"
                  className={`${styles.footerIconButton} ${!isWabaConnected ? styles.disabled : ""}`}
                  onClick={() => isWabaConnected && onOpenTeam?.()}
                  aria-label="Team"
                  disabled={!isWabaConnected}
                >
                  <RiGroupLine size={24} />
                </button>
              </Tooltip>
            )}

            <Tooltip 
              title={isWabaConnected ? "Subscription" : "Connect WhatsApp to enable subscription management"} 
              placement="top"
            >
              <button
                data-testid="sidebar-subscription-button"
                type="button"
                className={`${styles.footerIconButton} ${!isWabaConnected ? styles.disabled : ""}`}
                onClick={() => isWabaConnected && onOpenSubscription?.()}
                aria-label="Subscription"
                disabled={!isWabaConnected}
              >
                <RiBankCardLine size={24} />
              </button>
            </Tooltip>
          </>
        )}

        <Tooltip title="Settings">
          <button
            data-testid="sidebar-settings-button"
            type="button"
            className={styles.footerIconButton}
            onClick={() => onOpenSettings?.()}
            aria-label="Settings"
          >
            <RiSettings5Line size={24} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
